/**
 * The words and groupings of the chef Delivery page (the route). The plan
 * itself (order, leave time, arrivals, Maps links) comes computed from the
 * server (`api/delivery-route.ts`); this module turns it into copy, and turns
 * each stop's shipments into what to unload, grouped the way a van is packed.
 *
 * - Times read "8:55 AM"; windows "7:00 to 9:00 AM" (no dashes to parse on a
 *   phone, and never an em dash).
 * - What to unload is grouped by temperature (Hot, Chilled, Room: the hot box,
 *   the cooler, the dry box), each row tagged with its mealtime when the stop
 *   gets more than one, and with the day it is for when that is not the
 *   delivery day ("Breakfast for Fri"). The rows are the Orders rows.
 * - Phone numbers, in the contact field or inside the instructions, become
 *   tap-to-call links.
 * - Instructions are read line by line for their shape (a numbered step, a
 *   "Label: value" field, a note) so the page can weight the parts apart; the
 *   school's words are never rewritten.
 *
 * Pure; unit-tested.
 */

import type { DeliveryRouteDay, DeliveryRouteDriveStatus, DeliveryRouteStop } from '@/api/delivery-route'
import { describeDeliverable, formatWholeCount, unitizedLead } from '@/lib/chefs/deliverable-copy'
import { temperatureLabel } from '@/lib/chefs/format'
import { titleCaseFood } from '@/lib/chefs/kitchen-name'
import { pluralizePackagingName } from '@/lib/chefs/plural'
import { type DateRange, rangeWindow, startOfDay } from '@/lib/chefs/service-days'
import { sortDeliverables } from '@/lib/chefs/totals'
import type { Campus, Deliverable, SupplyLine, Temperature } from '@/lib/chefs/types'
import { MEALTIME_LABEL, MEALTIME_ORDER } from '@/lib/chefs/types'

import { CHEF_SERVICE_MATERIALS_VISIBLE } from '../../../../shared/chef-packaging-visibility'

// ---------------------------------------------------------------------------
// Clock and distance words
// ---------------------------------------------------------------------------

function parseClock(value: string | null | undefined): { hour: number; minute: number } | null {
  const match = value?.match(/^(\d{1,2}):(\d{2})$/)
  if (!match) {
    return null
  }
  const hour = Number(match[1])
  const minute = Number(match[2])
  return hour <= 23 && minute <= 59 ? { hour, minute } : null
}

function meridiemOf(hour: number): 'AM' | 'PM' {
  return hour < 12 ? 'AM' : 'PM'
}

function twelveHour(hour: number, minute: number): string {
  const h = hour % 12 === 0 ? 12 : hour % 12
  return `${h}:${String(minute).padStart(2, '0')}`
}

/** "08:55" → "8:55 AM". Null for anything that is not a clock time. */
export function clockLabel(value: string | null | undefined): string | null {
  const t = parseClock(value)
  return t ? `${twelveHour(t.hour, t.minute)} ${meridiemOf(t.hour)}` : null
}

/**
 * A window in words: "7:00 to 9:00 AM", "11:30 AM to 12:15 PM", "by 11:35 AM",
 * "from 9:30 AM", "at 9:30 AM". Null when it has no side.
 */
export function windowLabel(window: { start: string | null; end: string | null } | null | undefined): string | null {
  const start = parseClock(window?.start)
  const end = parseClock(window?.end)
  if (start && end) {
    if (start.hour === end.hour && start.minute === end.minute) {
      return `at ${clockLabel(window!.start)}`
    }
    return meridiemOf(start.hour) === meridiemOf(end.hour)
      ? `${twelveHour(start.hour, start.minute)} to ${clockLabel(window!.end)}`
      : `${clockLabel(window!.start)} to ${clockLabel(window!.end)}`
  }
  if (end) {
    return `by ${clockLabel(window!.end)}`
  }
  if (start) {
    return `from ${clockLabel(window!.start)}`
  }
  return null
}

/** 25 → "25 min"; 65 → "1 hr 5 min"; 60 → "1 hr". */
export function durationLabel(minutes: number): string {
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  if (hours === 0) {
    return `${rest} min`
  }
  return rest === 0 ? `${hours} hr` : `${hours} hr ${rest} min`
}

const METERS_PER_MILE = 1609.344

/** Miles, one decimal under ten ("4.2 mi", "17 mi"). */
export function distanceLabel(meters: number): string {
  const miles = meters / METERS_PER_MILE
  return miles < 10 ? `${Math.max(0.1, Math.round(miles * 10) / 10)} mi` : `${Math.round(miles)} mi`
}

/** "25 min drive · 17 mi"; null when the drive is unknown, or none (the same address). */
export function driveLabel(minutes: number | null, meters: number | null): string | null {
  if (minutes === null || minutes === 0) {
    return null
  }
  return meters === null
    ? `${durationLabel(minutes)} drive`
    : `${durationLabel(minutes)} drive · ${distanceLabel(meters)}`
}

// ---------------------------------------------------------------------------
// Phones and instructions
// ---------------------------------------------------------------------------

export interface PhoneLink {
  label: string
  /** `tel:+12485550170,12`: the comma pauses, then dials the extension. */
  href: string
}

/**
 * A US phone as typed ("2485550170 ext 12", "248-555-0147", "(734) 555-0186")
 * → "(248) 555-0170 ext. 12" and a tel: link. Null when there is no number.
 */
export function phoneLink(raw: string | null | undefined): PhoneLink | null {
  const text = raw?.trim()
  if (!text) {
    return null
  }
  const ext = text.match(/(?:ext\.?|extension|x)\s*(\d{1,6})\s*$/i)
  const main = ext ? text.slice(0, ext.index) : text
  let digits = main.replace(/\D/g, '')
  if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1)
  }
  if (digits.length !== 10) {
    const any = text.replace(/\D/g, '')
    return any.length >= 7 ? { label: text, href: `tel:${any}` } : null
  }
  const pretty = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  return ext
    ? { label: `${pretty} ext. ${ext[1]}`, href: `tel:+1${digits},${ext[1]}` }
    : { label: pretty, href: `tel:+1${digits}` }
}

/** The instructions as the school typed them, one line each, list bullets dropped. */
export function instructionLines(text: string | null | undefined): string[] {
  return (text ?? '')
    .split(/\r?\n/)
    .map((line) => line.trim().replace(/^[-*\u2022]\s+/, ''))
    .filter(Boolean)
}

/** One line of a school's instructions, read for its shape. */
export type InstructionItem =
  | { kind: 'step'; number: string; text: string }
  | { kind: 'field'; label: string; value: string }
  | { kind: 'note'; text: string }

/** "(1) Verify\u2026", "1) Verify\u2026", "1. Verify\u2026": a numbered step. */
const STEP_LINE = /^\(?(\d{1,2})[).]\s+(.+)$/
/** "Delivery Location: Kitchen Door": a short label of words, a colon, the value. Digits and commas never start a label. */
const FIELD_LINE = /^([A-Z][A-Za-z#&/ ]{0,39}?):\s+(.+)$/

/**
 * The instructions line by line, each read for its shape so the page can
 * weight the parts apart (Abner, Sep 25: "no difference in text color and
 * weight"): "(1) Verify\u2026" is step 1, "Delivery Location: Kitchen Door" is a
 * label and its value, anything else is a note. The school's words are never
 * changed, only split; a line typed twice shows once (Upper Campus types
 * "Delivery Location: Door #2" twice).
 */
export function instructionItems(text: string | null | undefined): InstructionItem[] {
  const seen = new Set<string>()
  const items: InstructionItem[] = []
  for (const line of instructionLines(text)) {
    const key = line.toLowerCase().replace(/\s+/g, ' ')
    if (seen.has(key)) {
      continue
    }
    seen.add(key)
    const step = STEP_LINE.exec(line)
    if (step) {
      items.push({ kind: 'step', number: step[1]!, text: step[2]!.trim() })
      continue
    }
    const field = FIELD_LINE.exec(line)
    if (field) {
      items.push({ kind: 'field', label: field[1]!.trim(), value: field[2]!.trim() })
      continue
    }
    items.push({ kind: 'note', text: line })
  }
  return items
}

export type InstructionPart = { kind: 'text'; text: string } | { kind: 'phone'; text: string; href: string }

const PHONE_IN_TEXT = /(?:\+?1[\s.-]?)?(?:\(\d{3}\)|\d{3})[\s.-]?\d{3}[\s.-]?\d{4}(?:\s*(?:ext\.?|x)\s*\d{1,6})?/gi

/** A line of instructions with every phone number in it made tappable. */
export function splitPhones(line: string): InstructionPart[] {
  const parts: InstructionPart[] = []
  let last = 0
  for (const match of line.matchAll(PHONE_IN_TEXT)) {
    const link = phoneLink(match[0])
    if (!link || !link.href.startsWith('tel:+1')) {
      continue
    }
    if (match.index > last) {
      parts.push({ kind: 'text', text: line.slice(last, match.index) })
    }
    parts.push({ kind: 'phone', text: match[0], href: link.href })
    last = match.index + match[0].length
  }
  if (last < line.length) {
    parts.push({ kind: 'text', text: line.slice(last) })
  }
  return parts
}

// ---------------------------------------------------------------------------
// Days
// ---------------------------------------------------------------------------

const SHORT_WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const SHORT_MONTH = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function utcDay(key: string): Date {
  return new Date(`${key}T00:00:00Z`)
}

/**
 * When food delivered today is served another day, say it once in words
 * (F4): "for Fri" within the week ahead, "for Mon, Sep 28" further out.
 * Null when every serve day is the delivery day.
 */
export function serviceDayPhrase(serveDays: readonly string[] | undefined, deliveryDate: string): string | null {
  const other = [...new Set(serveDays ?? [])].filter((d) => d !== deliveryDate).sort()
  if (other.length === 0) {
    return null
  }
  const words = other.map((key) => {
    const day = utcDay(key)
    const ahead = Math.round((day.getTime() - utcDay(deliveryDate).getTime()) / 86_400_000)
    const weekday = SHORT_WEEKDAY[day.getUTCDay()]!
    return ahead > 0 && ahead < 7 ? weekday : `${weekday}, ${SHORT_MONTH[day.getUTCMonth()]} ${day.getUTCDate()}`
  })
  return `for ${words.join(' and ')}`
}

/**
 * The day the Delivery page shows. A route is one day's drive (Abner, Sep 25:
 * "delivery routes are only useful on a single day"), so a week or a custom
 * window the chef chose on another chef page (the range is shared) opens as
 * one day: today when the window holds it, else the window's first day. A
 * daily range comes back as it is, so its identity stays stable for memos.
 */
export function routeDayRange(range: DateRange, today: Date): DateRange {
  if (range.granularity === 'daily') {
    return range
  }
  const { start, end } = rangeWindow(range)
  const day = startOfDay(today)
  return { granularity: 'daily', anchor: day >= start && day <= end ? day : start }
}

// ---------------------------------------------------------------------------
// What to unload at a stop
// ---------------------------------------------------------------------------

export type UnloadRow =
  | { kind: 'deliverable'; key: string; deliverable: Deliverable; tag: string | null }
  | { kind: 'supply'; key: string; line: SupplyLine; tag: string | null }

export interface UnloadSection {
  temperature: Temperature
  label: string
  rows: UnloadRow[]
}

const UNLOAD_TEMPERATURE_ORDER: Temperature[] = ['hot', 'chilled', 'ambient']

/**
 * The stop's contents in carriers: Hot, then Chilled, then Room, each in
 * mealtime order and the Orders row order within. A row is tagged with its
 * mealtime (and band, and the day it is for when that is not the delivery
 * day) only when the stop takes more than one mealtime or any of it is for
 * another day: a lunch-only stop reads with no tags. Supplies ride in Room.
 * Milk is not here: it is its own group on the stop (PROD-1502,
 * `routeMilkGroup` in `print-packages.ts`).
 */
export function stopUnloadSections(campus: Campus | undefined, deliveryDate: string): UnloadSection[] {
  if (!campus) {
    return []
  }
  const blocks = [...campus.mealtimes].sort(
    (a, b) => MEALTIME_ORDER.indexOf(a.mealtime) - MEALTIME_ORDER.indexOf(b.mealtime),
  )
  const anotherDay = blocks.some((b) => serviceDayPhrase(b.serveDays, deliveryDate) !== null)
  const needsTags = blocks.length > 1 || anotherDay
  const byTemperature = new Map<Temperature, UnloadRow[]>()
  const add = (temperature: Temperature, row: UnloadRow) =>
    byTemperature.set(temperature, [...(byTemperature.get(temperature) ?? []), row])
  for (const block of blocks) {
    const phrase = serviceDayPhrase(block.serveDays, deliveryDate)
    for (const d of sortDeliverables(block.deliverables)) {
      const parts = [phrase ? `${MEALTIME_LABEL[block.mealtime]} ${phrase}` : MEALTIME_LABEL[block.mealtime]]
      if (d.groupLabel) {
        parts.push(d.groupLabel)
      }
      add(d.temperature, { kind: 'deliverable', key: d.id, deliverable: d, tag: needsTags ? parts.join(' · ') : null })
    }
  }
  // Service materials are held with the chef supplies UI (`CHEF_SERVICE_MATERIALS_VISIBLE`).
  for (const line of CHEF_SERVICE_MATERIALS_VISIBLE ? campus.supplies : []) {
    add('ambient', { kind: 'supply', key: `supply-${line.skuId}`, line, tag: null })
  }
  return UNLOAD_TEMPERATURE_ORDER.filter((t) => byTemperature.has(t)).map((temperature) => ({
    temperature,
    label: temperatureLabel(temperature),
    rows: byTemperature.get(temperature)!,
  }))
}

// ---------------------------------------------------------------------------
// The summary and each stop, in words
// ---------------------------------------------------------------------------

export interface RouteSummaryCopy {
  /** "8:30 AM", or null when the leave time cannot be worked out. */
  leaveBy: string | null
  /** Why that time, or why there is none. */
  reason: string
  /** Something the chef has to act on (a missed window, no address). */
  warning: string | null
}

/** The leave time's reason: which stop sets it, and whether its window or its meal does. */
function bindingReason(day: DeliveryRouteDay): string {
  const stop = day.stops.find((s) => s.schoolLocationId === day.plan.bindingStopId)
  const deadline = clockLabel(stop?.deadline)
  if (!stop || !deadline) {
    return 'The latest the van can leave and still make every delivery window.'
  }
  const meal = stop.mealStarts.find((m) => m.time === stop.deadline && m.time !== stop.window?.end)
  if (meal) {
    return `to reach ${stop.name} before ${mealtimeWord(meal.mealtime)} starts at ${deadline}.`
  }
  return `to reach ${stop.name} before its window closes at ${deadline}.`
}

function mealtimeWord(mealtime: string): string {
  switch (mealtime) {
    case 'BREAKFAST':
      return 'breakfast'
    case 'AM_SNACK':
      return 'the morning snack'
    case 'LUNCH':
      return 'lunch'
    case 'SNACK':
    case 'PM_SNACK':
      return 'the afternoon snack'
    case 'SUPPER':
      return 'supper'
    default:
      return 'the meal'
  }
}

function names(list: readonly string[]): string {
  if (list.length <= 1) {
    return list.join('')
  }
  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`
}

export function routeSummaryCopy(day: DeliveryRouteDay, driveStatus: DeliveryRouteDriveStatus): RouteSummaryCopy {
  const missing = day.stops.filter((s) => day.missingAddressStopIds.includes(s.schoolLocationId)).map((s) => s.name)
  const missingWarning =
    missing.length > 0
      ? `${names(missing)} ${missing.length === 1 ? 'has' : 'have'} no address Google Maps can find, so ${missing.length === 1 ? 'it is' : 'they are'} not in the route link. Ordo support can fix the address.`
      : null
  if (day.plan.status === 'OK') {
    const late = day.stops.filter((s) => s.lateMinutes > 0).map((s) => s.name)
    return {
      leaveBy: clockLabel(day.plan.leaveBy),
      reason: bindingReason(day),
      warning:
        late.length > 0
          ? `In this order the van misses the window at ${names(late)}. Leaving earlier does not help: change the order or the time at each stop.`
          : missingWarning,
    }
  }
  if (day.plan.status === 'NO_TIME_WINDOWS') {
    return {
      leaveBy: null,
      reason:
        'No delivery windows on file for these schools, so there is no time to leave by. The stops are in the shortest drive order.',
      warning: missingWarning,
    }
  }
  const reason =
    driveStatus === 'NO_KITCHEN_ADDRESS'
      ? "Ordo needs your kitchen's address to work out when to leave. Ordo support can add it."
      : driveStatus === 'NOT_CONFIGURED'
        ? "Drive times aren't available yet, so the leave time can't be worked out. Be at each stop inside its window."
        : driveStatus === 'UNAVAILABLE'
          ? "Couldn't get drive times from Google Maps just now, so the leave time can't be worked out."
          : driveStatus === 'TOO_MANY_POINTS'
            ? 'Too many stops to work out drive times in one go.'
            : missing.length > 0
              ? `The leave time needs every stop's address: ${names(missing)} has none Google Maps can find.`
              : "Drive times aren't available, so the leave time can't be worked out."
  return { leaveBy: null, reason, warning: missingWarning }
}

/** How the stops got their order, in one line. */
export function orderLine(day: DeliveryRouteDay): string {
  if (day.order.source === 'SAVED') {
    return 'Your saved order'
  }
  return day.order.basis === 'DRIVE_TIMES' ? 'Fastest order that makes every window' : 'In delivery-window order'
}

export interface StopTimeCopy {
  /** The big number: when unloading starts, or the window's opening when the plan has no times. */
  time: string | null
  /** The window's close under it when `time` is its opening. */
  until: string | null
  /** "7 min early, wait for the window" / "Late by 2 min". */
  note: string | null
  tone: 'plain' | 'wait' | 'late'
}

export function stopTimeCopy(stop: DeliveryRouteStop): StopTimeCopy {
  if (stop.unloadAt) {
    if (stop.lateMinutes > 0) {
      const closes = clockLabel(stop.deadline)
      return {
        time: clockLabel(stop.unloadAt),
        until: null,
        note: closes
          ? `Late by ${durationLabel(stop.lateMinutes)}, closes ${closes}`
          : `Late by ${durationLabel(stop.lateMinutes)}`,
        tone: 'late',
      }
    }
    if (stop.waitMinutes > 0) {
      return {
        time: clockLabel(stop.unloadAt),
        until: null,
        note: `${durationLabel(stop.waitMinutes)} early, wait for the window`,
        tone: 'wait',
      }
    }
    return { time: clockLabel(stop.unloadAt), until: null, note: null, tone: 'plain' }
  }
  const start = clockLabel(stop.window?.start)
  const end = clockLabel(stop.window?.end)
  if (start && end && start !== end) {
    return { time: start, until: `to ${end}`, note: null, tone: 'plain' }
  }
  if (start || end) {
    return { time: start ?? `by ${end}`, until: null, note: null, tone: 'plain' }
  }
  return { time: null, until: null, note: null, tone: 'plain' }
}

/** "Lunch at 11:00 AM", when the stop's meals start (the ones served on the delivery day); null when none is known. */
export function mealStartsLine(stop: Pick<DeliveryRouteStop, 'mealStarts'>): string | null {
  const parts = stop.mealStarts.map((meal) => {
    const word = mealtimeWord(meal.mealtime)
    return `${word.charAt(0).toUpperCase()}${word.slice(1)} at ${clockLabel(meal.time)}`
  })
  return parts.length > 0 ? parts.join(' · ') : null
}

/** "Window 10:00 to 10:30 AM · Lunch at 11:00 AM", the stop's times in one line. */
export function stopTimesLine(stop: DeliveryRouteStop): string | null {
  const parts: string[] = []
  const window = windowLabel(stop.window)
  if (window) {
    parts.push(`Window ${window}`)
  }
  const meals = mealStartsLine(stop)
  if (meals) {
    parts.push(meals)
  }
  return parts.length > 0 ? parts.join(' · ') : null
}

/** "Received 10:52 AM" once the school has confirmed every shipment; "Partly received" before that. */
export function receivedLabel(received: DeliveryRouteStop['received'], timeZone: string): string | null {
  if (received.of === 0 || received.count === 0) {
    return null
  }
  if (received.count < received.of) {
    return 'Partly received'
  }
  if (!received.lastAt) {
    return 'Received'
  }
  const time = new Date(received.lastAt)
    .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone })
    .replace(/\u202f/g, ' ')
  return `Received ${time}`
}

// ---------------------------------------------------------------------------
// The timeline (Abner's pick B, Sep 25): the time column beside each node, the
// stop's tone, and what to deliver in carriers for the stop's side panel
// ---------------------------------------------------------------------------

/** The first letter up: the summary's reason reads "to reach …" after a time, and starts a line on the timeline. */
export function sentenceStart(text: string): string {
  return text.length === 0 ? text : `${text[0]!.toUpperCase()}${text.slice(1)}`
}

/**
 * A stop in one line, for the side panel's eyebrow: "Stop 1 · 8:55 AM · 25 min
 * drive". The time is when unloading starts (after any wait for the window);
 * with no plan it is the window ("Stop 1 · 7:00 to 9:00 AM"); the drive is the
 * one INTO the stop, left out when it is unknown or none. `drive: false` names
 * the stop, not the leg.
 */
export function stopNodeLabel(stop: DeliveryRouteStop, n: number, opts: { drive?: boolean } = {}): string {
  const parts = [`Stop ${n}`]
  const when = stop.unloadAt ? clockLabel(stop.unloadAt) : windowLabel(stop.window)
  if (when) {
    parts.push(when)
  }
  if ((opts.drive ?? true) && stop.driveMinutes) {
    parts.push(`${durationLabel(stop.driveMinutes)} drive`)
  }
  return parts.join(' · ')
}

/**
 * The column to the left of a node (Abner's pick, Sep 25: the times move into
 * a left column so they read straight down the page): `time` is the big word,
 * `detail` what qualifies it, under it.
 */
export interface TimeColumn {
  time: string | null
  detail: string | null
}

/** The leave node's column: "Leave" over "by 8:30 AM", or "Leave" alone when the time cannot be worked out. */
export function leaveTimeColumn(copy: Pick<RouteSummaryCopy, 'leaveBy'>): TimeColumn {
  return { time: 'Leave', detail: copy.leaveBy ? `by ${copy.leaveBy}` : null }
}

/**
 * A stop's column. With a plan: when unloading starts over the drive into the
 * stop ("8:55 AM" over "25 min drive"). Without: the window's opening over its
 * close ("7:00 AM" over "to 9:00 AM").
 */
export function stopTimeColumn(stop: DeliveryRouteStop): TimeColumn {
  const copy = stopTimeCopy(stop)
  if (copy.until) {
    return { time: copy.time, detail: copy.until }
  }
  const drive = stop.unloadAt && stop.driveMinutes ? `${durationLabel(stop.driveMinutes)} drive` : null
  return { time: copy.time, detail: drive }
}

/** The stop's marker: grey while it waits, green once the school confirmed every shipment, red when the plan arrives late. */
export type StopTone = 'pending' | 'done' | 'late'

export function stopTone(stop: Pick<DeliveryRouteStop, 'lateMinutes' | 'received'>): StopTone {
  if (stop.received.of > 0 && stop.received.count === stop.received.of) {
    return 'done'
  }
  return stop.lateMinutes > 0 ? 'late' : 'pending'
}

/** One thing a row puts on the van: the number (bold) and the words after it. */
export interface DeliverEntry {
  key: string
  /** "350", "11.1 gal", "2"; empty when nothing is known. */
  qty: string
  /** "Whole Grain Rich English Muffins", "Caesar Salads with Halal Grilled Chicken". */
  label: string
}

/**
 * What one deliverable puts on the van, in the Orders panel's "Deliver" words:
 * a unitized deliverable is its containers as a multiplier and its name ("2x"
 * + "Caesar Salad with Halal Grilled Chicken", `unitizedLead`; Abner, Sep 25:
 * "If it's unitized, it's X (meals) not Servings"); a bulk or loose one is
 * each food's total ("11.1 gal Shredded Lettuce", "350 Packets of Jam").
 */
export function deliverEntries(d: Deliverable): DeliverEntry[] {
  const copy = describeDeliverable(d)
  if (copy.mode === 'containers' && copy.count !== null) {
    return [{ key: d.id, ...unitizedLead(d, copy.count) }]
  }
  return copy.contents.map((line, i) => ({
    key: `${d.id}-${i}`,
    qty: line.qty,
    label: titleCaseFood(line.label),
  }))
}

function supplyEntry(line: SupplyLine): DeliverEntry {
  return {
    key: `supply-${line.skuId}`,
    qty: formatWholeCount(line.qty),
    label: pluralizePackagingName(line.name, line.qty),
  }
}

/** One row of a stop's "What to deliver": its words, its tag, and the deliverable it opens (none for supplies). */
export interface LoadRow {
  key: string
  /** One per food: a tray of bread and jam is one row of two lines. */
  entries: DeliverEntry[]
  /** "Lunch", "Breakfast for Mon"; null on a stop that takes one mealtime. */
  tag: string | null
  deliverable: Deliverable | null
}

export interface LoadSection {
  temperature: Temperature
  /** "Hot", "Chilled", "Room". */
  label: string
  rows: LoadRow[]
}

/**
 * The stop side panel's "What to deliver" (Abner's pick, Sep 25: grouped the
 * way it rides in the van): the carriers and tags of `stopUnloadSections`,
 * which the printed route sheet also reads, each row in the Orders panel's
 * "Deliver" words (`deliverEntries`).
 */
export function stopLoadSections(campus: Campus | undefined, deliveryDate: string): LoadSection[] {
  return stopUnloadSections(campus, deliveryDate).map((section) => ({
    temperature: section.temperature,
    label: section.label,
    rows: section.rows.map((row): LoadRow => {
      switch (row.kind) {
        case 'deliverable':
          return { key: row.key, entries: deliverEntries(row.deliverable), tag: row.tag, deliverable: row.deliverable }
        case 'supply':
          return { key: row.key, entries: [supplyEntry(row.line)], tag: row.tag, deliverable: null }
      }
    }),
  }))
}
